import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TextField, InputAdornment, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { PageLayout } from '../../PageLayout';
import {
  getSearchDetailsResultsSubject,
  getSearchDetailsLoadingSubject,
  fetchSearchDetails
} from '../../../store/Search';
import { PageCard } from '../../Compounds/CardWithTitle';
import { EntityType } from '../../../store/Search';
import { BasicDataGrid } from '../../Compounds/Table/BasicDataGrid';
import { DEFAULT_TABLE_STYLES } from '../../../utils/styleHelpers';
import { allCols as AssetCols } from '../../Compounds/Asset/AssetMiniTable';
import { allCols as ProvisionalAssetCols } from '../../Compounds/ProvisionalAsset/ProvisionalAssetMiniTable';
import { allCols as RPMAccountCols } from '../../Compounds/RPM/RPMMiniTable';
import { TicketCols, CustomerCols } from './ColumnDefs';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { GridCustomColDef } from '../../types';

const sxStyle = {
  ...DEFAULT_TABLE_STYLES.sx,
  minHeight: 'inherit',
  maxHeight: 'inherit',
  height: 'inherit',
  margin: '20px',
  '.MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': {
    marginLeft: 'auto'
  },
  borderTopLeftRadius: '0',
  borderTopRightRadius: '0'
};

const dataMap: Record<string, { title: string; primaryKey?: string; columns: GridCustomColDef[] }> = {
  [EntityType.ACCOUNT]: { title: 'RPM Accounts', primaryKey: 'accountId', columns: RPMAccountCols },
  [EntityType.ASSET]: { title: 'Assets', columns: AssetCols },
  [EntityType.PROVISIONAL_ASSET]: { title: 'Provisional Assets', columns: ProvisionalAssetCols },
  [EntityType.TICKET]: { title: 'Tickets', columns: TicketCols },
  [EntityType.CUSTOMER]: { title: 'Customers', columns: CustomerCols }
};

const Card = ({ type, rows }: { type: string; rows: object[] }) => {
  if (rows.length === 0) return null;
  const dataByType = dataMap[type] || { title: type };
  return (
    <PageCard
      title={dataByType.title}
      className="mb-5 mt-5"
      dataTestId={`search-results-card-${type}`}>
      <BasicDataGrid
        rows={rows}
        columns={dataByType.columns}
        sxStyle={sxStyle}
        disableColumnMenu
        showColumnVerticalBorder
        showCellVerticalBorder
        slotProps={{ toolbar: { showSearch: true } }}
        primaryKey={dataByType.primaryKey || 'id'}
        initialState={{
          sorting: {
            sortModel: [{ field: dataByType.columns[0].field, sort: 'asc' }]
          }
        }}
      />
    </PageCard>
  );
};

export const SearchPage = () => {
  const [inputValue, setInputValue] = useState<string>('');
  const [results, setResults] = useState<Record<string, object[]>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const query = searchParams.get('query');

  useEffect(() => {
    if (query) {
      setInputValue(query);
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('query');
      window.history.replaceState({}, '', currentUrl.toString());
      handleSubmit(undefined, query);
    }
  }, [query]);

  useEffect(() => {
    inputRef.current && inputRef.current.focus();
  }, []);

  const handleSubmit = (e?: React.FormEvent<HTMLFormElement>, newInputValue?: string) => {
    e?.preventDefault();
    const valueToUse = newInputValue !== undefined ? newInputValue : inputValue;
    if (!valueToUse.trim() || loading) return;
    fetchSearchDetails({ query: valueToUse.trim(), limit: 100, entityTypes: Object.keys(dataMap).join(',') });
  };

  useEffect(() => {
    const searchResultsSub = getSearchDetailsResultsSubject()?.subscribe((res) => {
      setResults(res);
    });
    const searchLoadingSub = getSearchDetailsLoadingSubject()?.subscribe((isLoading) => {
      setLoading(isLoading);
    });
    return () => {
      if (searchResultsSub) searchResultsSub.unsubscribe();
      if (searchLoadingSub) searchLoadingSub.unsubscribe();
    };
  }, []);

  return (
    <PageLayout
      className="w-full"
      dataTestId="search-page"
      dataCy="search-page-card"
      pageTitle="Search">
      <div
        data-testid="search-page-content"
        className="flex flex-col mt-10">
        <form
          onSubmit={handleSubmit}
          style={{ display: 'flex', gap: 8 }}>
          <TextField
            autoComplete="off"
            type="search"
            value={inputValue}
            placeholder=""
            onChange={(e) => setInputValue(e.target.value)}
            variant="outlined"
            name="search"
            size="small"
            inputRef={inputRef}
            slotProps={{
              htmlInput: { 'data-testid': 'search-input-field' },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }
            }}
            sx={{ width: '400px' }}
          />
          <Button
            variant="contained"
            disabled={!inputValue.trim() || loading}
            color="primary"
            size="small"
            type="submit">
            Search
          </Button>
        </form>
        <div className="mt-6 text-gray-600">
          {loading && (
            <div className="flex h-[35rem] m-auto">
              <ComponentSpinner label="Loading data..." />
            </div>
          )}
          {!loading &&
            Object.keys(dataMap).map((type) => (
              <Card
                key={type}
                type={type}
                rows={results[type] || []}
              />
            ))}
        </div>
      </div>
    </PageLayout>
  );
};
